import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Search, FileText, Download, Trash2 } from "lucide-react";
import { api, API, formatError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function DocumentHub() {
  const { isAdmin } = useAuth();
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [tags, setTags] = useState("");
  const fileRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/documents", { params: q ? { q } : {} });
      setDocs(data);
    } catch (e) { toast.error(formatError(e)); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Only PDF files"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tags", tags);
    setUploading(true);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setTags(""); fileRef.current.value = "";
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.filename}?`)) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const fmt = (b) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`;

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#DC2A1C] font-semibold mb-1">Resources</div>
        <h1 className="text-3xl font-black tracking-tight">Document Hub</h1>
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
        <div className="relative md:w-96">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by filename or tag..."
            className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg pl-10 pr-4 py-2.5 focus:outline-none focus:border-[#DC2A1C]"
            data-testid="docs-search"
          />
        </div>
        {isAdmin && (
          <div className="flex gap-2 items-center">
            <input
              placeholder="Tags (comma separated)"
              value={tags} onChange={(e) => setTags(e.target.value)}
              className="bg-[#F8F9FA] border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="docs-tags"
            />
            <label className={`tl-btn flex items-center gap-2 cursor-pointer ${uploading ? "opacity-60" : ""}`} data-testid="docs-upload-btn">
              <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload PDF"}
              <input type="file" accept=".pdf" ref={fileRef} onChange={onUpload} disabled={uploading} className="hidden" />
            </label>
          </div>
        )}
      </div>

      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white divide-y divide-gray-100" data-testid="docs-list">
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-4 p-4 hover:bg-[#F8F9FA]">
            <div className="w-10 h-10 rounded-lg bg-[#DC2A1C]/10 grid place-items-center flex-shrink-0"><FileText className="w-5 h-5 text-[#DC2A1C]" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{d.filename}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {fmt(d.size)} · {new Date(d.uploaded_at).toLocaleDateString()} · by {d.uploaded_by}
                {d.tags && <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-100">{d.tags}</span>}
              </div>
            </div>
            <button onClick={() => download(d)} className="p-2 rounded hover:bg-gray-100" data-testid={`download-${d.id}`}><Download className="w-4 h-4" /></button>
            {isAdmin && (
              <button onClick={() => remove(d)} className="p-2 rounded hover:bg-red-50 text-red-600" data-testid={`delete-doc-${d.id}`}><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
        {docs.length === 0 && <div className="p-10 text-center text-sm text-gray-500">No documents found.</div>}
      </div>
    </div>
  );
}
